import { apiService } from './api';

export interface Strategy {
  id: string;
  name: string;
  description?: string;
  query: string;
  is_active: boolean;
  notification_enabled: boolean;
  execution_frequency: 'hourly' | 'daily' | 'weekly';
  schedule_type?: string;
  created_at: string;
  updated_at: string;
  last_executed_at?: string;
  created_by: string;
  executions?: StrategyExecution[];
}

export interface StrategyExecution {
  id: string;
  strategy_id: string;
  strategy_name: string;
  status: 'running' | 'completed' | 'failed';
  started_at: string;
  completed_at?: string;
  duration_seconds?: number;
  result_count?: number;
  changes_detected?: number;
  error_message?: string;
  results?: any[];
  changes?: {
    added: any[];
    removed: any[];
  };
}

export interface StrategyTestResult {
  count: number;
  sample: any[];
  execution_time: number;
  query: string;
}

export interface CreateStrategyData {
  name: string;
  description?: string;
  query: string;
  is_active: boolean;
  notification_enabled: boolean;
  execution_frequency: 'hourly' | 'daily' | 'weekly';
}

export interface UpdateStrategyData {
  name?: string;
  description?: string;
  query?: string;
  is_active?: boolean;
  notification_enabled?: boolean;
  execution_frequency?: 'hourly' | 'daily' | 'weekly';
}

class StrategyService {
  async getStrategies(): Promise<Strategy[]> {
    const response = await apiService.get<{ strategies: Strategy[] }>('/strategies');
    return response.strategies;
  }

  async getStrategy(id: string): Promise<Strategy> {
    const response = await apiService.get<{ strategy: Strategy }>(`/strategies/${id}`);
    return response.strategy;
  }

  async createStrategy(data: CreateStrategyData): Promise<Strategy> {
    const response = await apiService.post<{ strategy: Strategy }>('/strategies', data);
    return response.strategy;
  }

  async updateStrategy(id: string, data: UpdateStrategyData): Promise<Strategy> {
    const response = await apiService.put<{ strategy: Strategy }>(`/strategies/${id}`, data);
    return response.strategy;
  }

  async deleteStrategy(id: string): Promise<void> {
    await apiService.delete(`/strategies/${id}`);
  }

  async runStrategy(id: string): Promise<StrategyExecution> {
    const response = await apiService.post<{ execution: StrategyExecution }>(`/strategies/${id}/execute`);
    return response.execution;
  }

  async executeStrategy(id: string): Promise<StrategyExecution> {
    return this.runStrategy(id);
  }

  async testStrategy(query: string): Promise<StrategyTestResult> {
    const response = await apiService.post<StrategyTestResult>('/strategies/test', { query });
    return response;
  }

  async getStrategyExecutions(strategyId: string): Promise<StrategyExecution[]> {
    const response = await apiService.get<{ executions: StrategyExecution[] }>(`/strategies/${strategyId}/executions`);
    return response.executions;
  }

  async getRecentExecutions(limit: number = 20): Promise<StrategyExecution[]> {
    const response = await apiService.get<{ executions: StrategyExecution[] }>(`/strategies/executions?limit=${limit}`);
    return response.executions;
  }

  async getExecution(executionId: string): Promise<StrategyExecution & { results: any[] }> {
    // Get execution results which includes execution metadata
    const resultsResponse = await apiService.get<{ results: any[] }>(`/strategies/executions/${executionId}/results`);
    
    // For now, we'll create a mock execution object since there's no single endpoint
    // In a real implementation, you'd want to add a backend endpoint for this
    const execution: StrategyExecution & { results: any[] } = {
      id: executionId,
      strategy_id: '', // This would come from a proper endpoint
      strategy_name: 'Unknown Strategy',
      status: 'completed',
      started_at: new Date().toISOString(),
      completed_at: new Date().toISOString(),
      duration_seconds: 0,
      result_count: resultsResponse.results.length,
      error_message: undefined,
      results: resultsResponse.results
    };
    
    return execution;
  }

  async getExecutionResults(executionId: string): Promise<any[]> {
    const response = await apiService.get<{ results: any[] }>(`/strategies/executions/${executionId}/results`);
    return response.results;
  }

  async getExecutionChanges(executionId: string): Promise<{ added: any[]; removed: any[] }> {
    const response = await apiService.get<{ changes: { added: any[]; removed: any[] } }>(`/strategies/executions/${executionId}/changes`);
    return response.changes;
  }

  // Telegram notification settings
  async updateTelegramSettings(settings: { bot_token?: string; chat_id?: string; enabled?: boolean }): Promise<void> {
    await apiService.put('/strategies/telegram-settings', settings);
  }

  async getTelegramSettings(): Promise<{ bot_token?: string; chat_id?: string; enabled: boolean }> {
    const response = await apiService.get<{ settings: { bot_token?: string; chat_id?: string; enabled: boolean } }>('/strategies/telegram-settings');
    return response.settings;
  }

  async testTelegramNotification(): Promise<void> {
    await apiService.post('/strategies/telegram-test');
  }

  // Strategy statistics
  async getStrategyStats(strategyId: string): Promise<{
    total_executions: number;
    successful_executions: number;
    average_execution_time: number;
    average_results_count: number;
    last_execution: StrategyExecution | null;
  }> {
    const response = await apiService.get<{
      total_executions: number;
      successful_executions: number;
      average_execution_time: number;
      average_results_count: number;
      last_execution: StrategyExecution | null;
    }>(`/strategies/${strategyId}/stats`);
    return response;
  }

  // Get available variables for autocomplete
  async getAvailableVariables(): Promise<{
    [category: string]: {
      [variable: string]: {
        type: string;
        description: string;
        example?: any;
      };
    };
  }> {
    const response = await apiService.get<{
      [category: string]: {
        [variable: string]: {
          type: string;
          description: string;
          example?: any;
        };
      };
    }>('/strategies/variables');
    return response;
  }

  // Delete execution
  async deleteExecution(executionId: string): Promise<{ message: string }> {
    return apiService.delete(`/strategies/executions/${executionId}`);
  }
}

export const strategyService = new StrategyService();
